import { useCallback, useEffect, useRef, useState } from 'react';

import { closeAllDropdowns } from '../useDropdownGroup';
import { SUBMENU_CLOSE_DELAY } from './constants';

export type MenuId = 'text' | 'columns' | 'other' | 'confidence';

export const useMenuState = () => {
  const [openMenu, setOpenMenu] = useState<MenuId | null>(null);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const [submenuPosition, setSubmenuPosition] = useState({ top: 0, left: 0 });
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    return clearCloseTimeout;
  }, [clearCloseTimeout]);

  const toggleMenu = useCallback((menu: MenuId) => {
    setOpenMenu(prev => {
      if (prev === menu) return null;
      closeAllDropdowns();
      return menu;
    });
    setOpenSubmenu(null);
  }, []);

  const closeAllMenus = useCallback(() => {
    setOpenMenu(null);
    setOpenSubmenu(null);
  }, []);

  const handleSubmenuOpen = useCallback(
    (title: string, element: Element) => {
      clearCloseTimeout();
      const rect = element.getBoundingClientRect();
      setSubmenuPosition({ top: rect.top, left: rect.right + 8 });
      setOpenSubmenu(title);
    },
    [clearCloseTimeout],
  );

  const handleSubmenuClose = useCallback(() => {
    closeTimeoutRef.current = setTimeout(() => {
      setOpenSubmenu(null);
    }, SUBMENU_CLOSE_DELAY);
  }, []);

  return {
    openMenu,
    openSubmenu,
    submenuPosition,
    toggleMenu,
    closeAllMenus,
    clearCloseTimeout,
    handleSubmenuOpen,
    handleSubmenuClose,
  };
};
